import { Truck, XCircle } from 'lucide-react';
import { AmountSelector } from './AmountSelector';
import { useGiftCardForm, RECIPIENT_FORM_FIELDS } from '../hooks/useGiftCardForm';
import { formatPrice } from '@/shared/utils/priceFormatter';

export const GiftCardForm = () => {
  const {
    config,
    isConfigLoading,
    configError,
    formData,
    fieldErrors,
    isCheckingNdd,
    hasNddError,
    pincodeErrorMessage,
    estimatedDeliveryDate,
    handleChange,
    handleAmountSelect,
    handlePincodeCheckClick,
    handleSubmit,
  } = useGiftCardForm();

  const labelStyle = 'absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 bg-white px-1 transition-all duration-200 pointer-events-none peer-focus:-top-0 peer-focus:text-xs peer-focus:text-[#002855] peer-focus:font-medium peer-[:not(:placeholder-shown)]:-top-0 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:text-[#002855] peer-[:not(:placeholder-shown)]:font-medium';

  const renderInlineError = (errorMsg?: string, extraRight = 'right-3') =>
    errorMsg ? (
      <div className={`absolute ${extraRight} top-1/2 -translate-y-1/2 pointer-events-none z-10`}>
        <span className="text-red-500 text-xs font-medium bg-white px-1">{errorMsg}</span>
      </div>
    ) : null;

  const showEddBanner = Boolean(estimatedDeliveryDate && !pincodeErrorMessage);
  const showPincodeError = Boolean(pincodeErrorMessage);

  return (
    <form onSubmit={handleSubmit} noValidate className="flex-1 flex flex-col justify-center p-4 sm:p-6 md:p-8">
      <h1 className="text-xl font-serif font-bold text-[#002855] mb-3">
        Enter Gift Card Amount
      </h1>

      {configError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-xs rounded">
          {configError}
        </div>
      )}

      <div className="mb-2">
        <div className="relative">
          <input
            type="number"
            name="amount"
            id="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder=" "
            className={`peer w-full border p-3 text-sm rounded-md outline-none bg-transparent pr-28 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
              fieldErrors.amount ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <label htmlFor="amount" className={labelStyle}>
            Amount <span className="text-red-500">*</span>
          </label>
          {renderInlineError(fieldErrors.amount)}
        </div>
        <p className="text-xs text-gray-400 mt-1">Min. {formatPrice(1000)} | Max. {formatPrice(50000)}</p>
      </div>

      <div className="relative my-4 flex items-center justify-center">
        <div className="border-t border-gray-200 w-full" />
        <span className="bg-white px-3 text-xs text-gray-400 font-medium absolute">OR</span>
      </div>

      <AmountSelector
        displayAmounts={config?.displayAmounts || []}
        isLoading={isConfigLoading}
        onSelectAmount={handleAmountSelect}
      />

      <div className="space-y-4 mt-4">
        {RECIPIENT_FORM_FIELDS.map((field) => (
          <div key={field.name} className="relative">
            <input
              type={field.type}
              name={field.name}
              id={field.name}
              maxLength={field.maxLength}
              value={formData[field.name]}
              onChange={handleChange}
              placeholder=" "
              className={`peer w-full border p-3 text-sm rounded-md outline-none bg-transparent ${
                fieldErrors[field.name] ? 'border-red-500 pr-32' : 'border-gray-300'
              }`}
            />
            <label htmlFor={field.name} className={labelStyle}>
              {field.label} <span className="text-red-500">*</span>
            </label>
            {renderInlineError(fieldErrors[field.name])}
          </div>
        ))}

        <div>
          <div className="relative flex border border-gray-300 rounded-md">
            <input type="text" name="pincode" id="pincode" placeholder='' maxLength={6} value={formData.pincode} onChange={handleChange} className="peer flex-1 p-3 text-sm outline-none bg-transparent pr-28"/>
            <label htmlFor="pincode" className={labelStyle}>
              Enter Pincode <span className="text-red-500">*</span>
            </label>

            {renderInlineError(fieldErrors.pincode, 'right-[100px]')}

            <button
              type="button"
              onClick={handlePincodeCheckClick}
              disabled={isCheckingNdd || formData.pincode.trim().length !== 6}
              className="absolute right-0 h-full bg-[#622335] text-white px-4 text-xs font-medium hover:bg-[#4d1b29] disabled:opacity-50 rounded-r-[5px]"
            >
              Check EDD
            </button>
          </div>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showPincodeError || showEddBanner
                ? 'max-h-24 opacity-100 mt-3 md:mt-3 md:max-h-none md:min-h-[50px]'
                : 'max-h-0 opacity-0 mt-0 pointer-events-none md:mt-3 md:max-h-none md:min-h-[50px] md:opacity-100'
              }`}
          >
            {showPincodeError && (
              <div className="w-full p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-600 text-xs font-medium animate-fadeIn">
                <XCircle size={16} className="flex-shrink-0 text-red-500" />  <span className="leading-relaxed">Sorry! This area is currently not serviceable.</span> {/*pincodeErrorMessage*/}
              </div>
            )}

            {showEddBanner && (
              <div className="w-full p-3 bg-[#FFF5F6] border border-[#622335]/30 rounded-md flex items-center gap-2 text-[#622335] text-xs font-medium animate-fadeIn">
                <Truck size={16} className="flex-shrink-0 text-[#622335]" />
                <span>
                  Your Estimated Date Of Delivery: <strong>{estimatedDeliveryDate}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isCheckingNdd || isConfigLoading || hasNddError || !config?.customizationId}
        className="w-full mt-4 bg-[#622335] text-white py-3.5 px-4 font-medium text-sm rounded-md hover:bg-[#4d1b29] transition-colors disabled:opacity-50"
      >
        Proceed To Payment
      </button>
    </form>
  );
};

export default GiftCardForm;